import { PackageNameEnum } from '@electricjs/core_entity-client';
import { ISOLVED_SEAT_URL, ISOLVED_TIER_URL } from '@/constants/urls';

// Customer segments that get redirected to support instead of self-serve flows.
// Add a new key here (plus its detection in useCustomerRedirectSegment) to onboard another segment.
export type CustomerRedirectSegment = 'isolved';

export type CustomerRedirectParams = {
  organizationId: string;
  hcmIntegrationId?: string;
  currentPlan?: PackageNameEnum;
  currentSeatCount?: number;
};

type CustomerRedirectUrls = {
  planInterestUrl: (params: CustomerRedirectParams) => string;
  licenseExpansionUrl: (params: CustomerRedirectParams) => string;
};

type RequestType = 'Tier Increase' | 'Seat Expansion';

// Query param keys the receiving forms expect — shared across segments.
const CUSTOMER_REDIRECT_PARAM_KEYS = {
  ORGANIZATION_ID: 'ith_organization_id',
  HCM_INTEGRATION_ID: 'ith_hcm_integration_id',
  CURRENT_PLAN: 'rqst__current_ith_plan',
  CURRENT_SEAT_COUNT: 'rqst__current_seat_count',
  REQUEST_TYPE: 'rqst__request_type',
} as const;

// Maps our internal plan enum to the exact plan labels the receiving form expects.
const ISOLVED_PLAN_LABELS: Partial<Record<PackageNameEnum, string>> = {
  [PackageNameEnum.IsolvedFree]: 'isolvedHR',
  [PackageNameEnum.IsolvedEssentials]: 'Essentials',
  [PackageNameEnum.IsolvedPro]: 'Pro',
};

const buildRedirectUrl = (
  baseUrl: string,
  requestType: RequestType,
  params: CustomerRedirectParams
) => {
  const searchParams = new URLSearchParams();
  searchParams.set(
    CUSTOMER_REDIRECT_PARAM_KEYS.ORGANIZATION_ID,
    params.organizationId
  );
  if (params.hcmIntegrationId) {
    searchParams.set(
      CUSTOMER_REDIRECT_PARAM_KEYS.HCM_INTEGRATION_ID,
      params.hcmIntegrationId
    );
  }
  if (params.currentPlan) {
    searchParams.set(
      CUSTOMER_REDIRECT_PARAM_KEYS.CURRENT_PLAN,
      ISOLVED_PLAN_LABELS[params.currentPlan] ?? params.currentPlan
    );
  }
  if (params.currentSeatCount !== undefined) {
    searchParams.set(
      CUSTOMER_REDIRECT_PARAM_KEYS.CURRENT_SEAT_COUNT,
      String(params.currentSeatCount)
    );
  }
  searchParams.set(CUSTOMER_REDIRECT_PARAM_KEYS.REQUEST_TYPE, requestType);

  return `${baseUrl}?${searchParams.toString()}`;
};

export const CUSTOMER_REDIRECT_URLS: Record<
  CustomerRedirectSegment,
  CustomerRedirectUrls
> = {
  isolved: {
    planInterestUrl: params =>
      buildRedirectUrl(ISOLVED_TIER_URL, 'Tier Increase', params),
    licenseExpansionUrl: params =>
      buildRedirectUrl(ISOLVED_SEAT_URL, 'Seat Expansion', params),
  },
};
